import { ROADMAP_ASSISTANT_SCHEMA } from "./roadmapAssistantConstants";

export type CursorJsonReplyPromptOptions = {
  /** When false, prompt warns not to send workingNotes-only pastes while dashboard is locked. */
  includeWorkingNotesInPaste: boolean;
};

/**
 * Short instructions to paste into Cursor so the model returns one ```json``` block
 * the dashboard "Apply assistant paste" field can consume.
 */
export function buildCursorJsonReplyPrompt(
  itemId: string,
  options: CursorJsonReplyPromptOptions
): string {
  const { includeWorkingNotesInPaste } = options;

  const lockNote = includeWorkingNotesInPaste
    ? ""
    : [
        "**Dashboard note:** Official edits are **locked** on my side. **Do not** return a JSON object that **only** updates `workingNotes` — I cannot apply that until I unlock. You may still return `workingNotes` together with other allowed fields (subtasks, `expectedFinishAt`, company questions, etc.).",
        "",
      ].join("\n");

  const exampleTail = includeWorkingNotesInPaste
    ? [
        "```json",
        "{",
        `  "schema": "${ROADMAP_ASSISTANT_SCHEMA}",`,
        `  "itemId": "${itemId}",`,
        `  "workingNotes": "Session summary: what changed, files touched, what is next."`,
        "}",
        "```",
      ].join("\n")
    : [
        "```json",
        "{",
        `  "schema": "${ROADMAP_ASSISTANT_SCHEMA}",`,
        `  "itemId": "${itemId}",`,
        `  "expectedFinishAt": "2026-04-15T12:00:00.000Z",`,
        `  "subtasks": [`,
        `    { "id": "keep-existing-uuid-from-bundle", "title": "Updated title", "done": true }`,
        `  ]`,
        "}",
        "```",
      ].join("\n");

  return [
    "I pasted our **roadmap task bundle** from the admin dashboard. After you complete the work it describes:",
    "",
    "1. Reply with **one** markdown fence labeled `json` containing **a single JSON object** (a short sentence outside the fence is fine, but the fenced block must be easy to copy in one selection).",
    "2. That object **must** include exactly:",
    `   - \`"schema": "${ROADMAP_ASSISTANT_SCHEMA}"\``,
    `   - \`"itemId": "${itemId}"\` (this UUID must match the bundle — do not change it).`,
    "3. Add **only** optional keys you actually changed; omit everything else:",
    "   - `workingNotes` (string)",
    "   - `expectedFinishAt` (ISO string or `null`)",
    "   - `subtasks` — existing rows: include `id` plus any of `title`, `done`, `expectedFinishAt` (you may omit `title` if you only toggle `done` or change the date). New rows: `title` required, no `id`.",
    "   - `removeSubtaskIds` (string[])",
    "   - `companyQuestions` — merge by `id`, or new with `question`; optional `resolved`",
    "   - `removeCompanyQuestionIds` (string[])",
    "",
    lockNote,
    "I will paste your ```json block into **Apply assistant paste** in the roadmap task modal.",
    "",
    "Minimal example (adjust keys; remove what you did not change):",
    exampleTail,
  ]
    .filter(Boolean)
    .join("\n");
}
